import { useClearDispatch } from '@contexts/auth/AuthContext';
import { setSafeLocalStorageItem } from '@utils/localStorage/getSafeLocalStorage';
import { SSO_AUTH_REDIRECT_KEYS } from '@constants/constants';
import { getLogoutRedirectionURL } from '@components/ui/headers/components/helpers';
import { AuthSyncType, useAuthTabSync } from '@utils/auth/useAuthTabSync';

import { useAuthWrapper } from './useAuthWrapper';

/**
 * @description The hook automatically detects whether the user is authenticated via SSO
 * and applies the appropriate logout strategy. It clears user information from the
 * application state and localStorage, then handles redirection based on the user type.
 * When logout occurs, it notifies all other browser tabs to sync their state.
 *
 * @returns An object containing the logout function
 * @returns {Object} result - The hook return object
 * @returns {Function} result.logout - Async function to perform logout with optional redirect
 */
export const useLogout = () => {
  const { isAuthenticated, signOut } = useAuthWrapper();
  const clearUserInformation = useClearDispatch();
  const { broadcastAuthChange } = useAuthTabSync();

  const logout = async (redirectPath?: string) => {
    // Notify other tabs about the logout event *before* clearing local state.
    // This prevents a race condition where the channel closes before the message is sent.
    broadcastAuthChange(AuthSyncType.LOGOUT);

    // We check for SSO authentication status *before* clearing everything.
    const isSsoUser = isAuthenticated;

    await clearUserInformation();

    if (isSsoUser) {
      // For SSO users, we need to set the redirect key and then call the signout method from oidc-client.
      setSafeLocalStorageItem(
        SSO_AUTH_REDIRECT_KEYS.LOGOUT,
        `${getLogoutRedirectionURL(
          '/logout',
          window.location.pathname ?? ''
        )}` || '/'
      );
      await signOut();
    } else {
      if (redirectPath) {
        window.location.href = redirectPath;
      } else {
        window.location.reload();
      }
    }
  };

  return { logout };
};
